import { differenceInMilliseconds } from 'date-fns/differenceInMilliseconds'
import { isPast } from 'date-fns/isPast'
import { parseISO } from 'date-fns/parseISO'
import { storeToRefs } from 'pinia'
import { onBeforeMount, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'

import { type AuthLogoutEventPayload } from '@/auth/types'
import { useAuthActions } from '@/auth/useAuthActions'
import { useAuthStore } from '@/auth/useAuthStore'
import { logError } from '@/logger'
import { useEmitter as messageListener } from '@/utils/useEmitter'

// 2147483647 is the max setTimeout value (32 bit integer value)
const TIMEOUT_MAX = 2147483647

export const useSessionManager = (): void => {
  let timeout: number | null = null

  const { logout } = useAuthActions()
  const router = useRouter()

  const authStore = useAuthStore()
  const { expiresAt, isAuthenticated } = storeToRefs(authStore)

  // Guards against the concurrent brand-scoped 403s that fan out on a stale
  // tab (e.g. plans + capabilities + domains all denied at once) each kicking
  // off their own recovery + redirect.
  let recoveringBrandAccess = false

  let recoveringCsrf = false

  const onBrandAccessLost = async (): Promise<void> => {
    if (recoveringBrandAccess) return
    recoveringBrandAccess = true
    try {
      const recovered = await authStore.ensureAccessibleBrandSession()
      if (recovered) {
        await router.replace(authStore.home)
      }
    } catch (error) {
      // The emitter doesn't await this handler, so a rejected redirect
      // (e.g. a navigation guard aborting) would escape unobserved.
      logError(error, { context: 'onBrandAccessLost' })
    } finally {
      recoveringBrandAccess = false
    }
  }

  // A stale-CSRF 403 means the shared `partner_at` cookie rotated under this
  // origin's cached token. The cookie is still valid but the client can't
  // re-derive the CSRF token (secret is server-only), so recover with one
  // logout. Two guards, because this composable lives on the App root and
  // survives the redirect to /login: `recoveringCsrf` dedupes the synchronous
  // 403 fan-out of one recovery, and the `isAuthenticated` check drops any
  // stragglers that resolve after logout — together they collapse the burst to
  // a single logout. Releasing the guard in `finally` still lets a fresh
  // session that later goes stale recover again rather than looping.
  const onCsrfInvalid = async (
    payload?: AuthLogoutEventPayload
  ): Promise<void> => {
    // Accepted edge: a pre-logout request whose 403 lands only after a full
    // logout + re-login would log the fresh session out once. Distinguishing it
    // needs per-request session identity — not worth it for this stopgap; the
    // gateway CSRF work is the real fix.
    if (!isAuthenticated.value) return
    if (recoveringCsrf) return
    recoveringCsrf = true
    const { source, ...detail } = payload ?? {}
    try {
      await logout({
        detail: { ...detail },
        reason: source ?? 'csrf-invalid',
      })
    } catch (error) {
      logError(error, { context: 'onCsrfInvalid' })
    } finally {
      recoveringCsrf = false
    }
  }

  // Maps the server-side trigger carried on the emitter event to a logout
  // reason. Kept as a stable reference so the matching `off` in onBeforeUnmount
  // removes the same handler.
  const onAuthLogout = (payload?: AuthLogoutEventPayload): void => {
    // `source` becomes the canonical `reason`; keep the rest as diagnostic
    // detail so the event doesn't carry a redundant `source` sibling.
    const { source, ...detail } = payload ?? {}
    void logout({
      detail: { ...detail },
      reason: source ?? 'auth-logout-event',
    })
  }

  const onTimoutTriggered = async (): Promise<void> => {
    if (expiresAt.value && isPast(expiresAt.value)) {
      await logout({
        detail: { expiresAt: expiresAt.value },
        reason: 'session-timer-expired',
      })
    } else {
      registerTimeout()
    }
  }

  const registerTimeout = (): void => {
    if (timeout) window.clearTimeout(timeout)

    if (expiresAt.value) {
      const timeoutDuration = differenceInMilliseconds(
        parseISO(expiresAt.value),
        new Date()
      )

      timeout = window.setTimeout(
        onTimoutTriggered,

        Math.min(timeoutDuration, TIMEOUT_MAX)
      )
    }
  }

  watch(isAuthenticated, async (isAuthed) => {
    if (isAuthed) {
      registerTimeout()
    } else {
      // Snapshot the raw auth state *before* logout() nulls it, so the
      // `auth.logout` funnel can attribute the (network-less, single-tab)
      // client-state de-auth we see on the auth-service provider: which ref
      // went null while the session was still valid, and whether a cross-tab
      // storage clear immediately preceded it. Diagnostic only — remove once
      // the cause is found.
      await logout({
        detail: { ...authStore.authStateSnapshot },
        reason: 'isauthenticated-false',
      })
    }
  })

  onBeforeMount(async () => {
    if (isAuthenticated.value) {
      registerTimeout()
    }

    messageListener.on('auth.logout', onAuthLogout)
    messageListener.on('auth.brand-access-lost', onBrandAccessLost)
    messageListener.on('auth.csrf-invalid', onCsrfInvalid)
  })

  onBeforeUnmount(() => {
    if (timeout) window.clearTimeout(timeout)
    messageListener.off('auth.logout', onAuthLogout)
    messageListener.off('auth.brand-access-lost', onBrandAccessLost)
    messageListener.off('auth.csrf-invalid', onCsrfInvalid)
  })
}
